import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { Button } from "@/components/ui/button";
import api, { money } from "@/lib/api";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [booking, setBooking] = useState(null);

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= 8) { setState("pending"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setBooking(data.booking);
      if (data.payment_status === "paid") { setState("paid"); return; }
      if (data.status === "expired") { setState("error"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-sm p-8 md:p-10 text-center" data-testid="booking-success-card">
        <Link to="/" className="flex items-center justify-center gap-2 font-serif text-2xl font-semibold text-foreground mb-8">
          <Saguaro className="h-7 w-7 text-primary" /> The East Valley
        </Link>

        {state === "checking" && (
          <>
            <Loader2 className="h-14 w-14 text-primary animate-spin mx-auto mb-5" />
            <h1 className="font-serif text-3xl font-semibold text-foreground mb-2">Confirming your payment…</h1>
            <p className="text-muted-foreground">Hang tight, this only takes a moment.</p>
          </>
        )}

        {state === "paid" && (
          <div data-testid="booking-confirmed">
            <CheckCircle2 className="h-16 w-16 text-secondary mx-auto mb-5" />
            <h1 className="font-serif text-3xl font-semibold text-foreground mb-2">You're all booked! 🎉</h1>
            <p className="text-muted-foreground mb-6">A confirmation has been sent to your email. We can't wait to host you.</p>
            {booking && (
              <div className="bg-accent/50 rounded-xl p-5 text-left space-y-2 text-sm mb-6">
                <div className="flex justify-between"><span className="text-muted-foreground">Check-in</span><span className="font-medium">{booking.check_in}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Check-out</span><span className="font-medium">{booking.check_out}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Guests</span><span className="font-medium">{booking.guests}</span></div>
                <div className="flex justify-between border-t border-border pt-2"><span className="text-muted-foreground">Deposit paid</span><span className="font-semibold text-primary">{money(booking.deposit_amount, booking.currency)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Balance at check-in</span><span className="font-medium">{money(booking.balance_due, booking.currency)}</span></div>
              </div>
            )}
            <Button asChild className="rounded-full bg-primary w-full h-11"><Link to="/">Back to home</Link></Button>
          </div>
        )}

        {state === "pending" && (
          <>
            <Loader2 className="h-14 w-14 text-primary animate-spin mx-auto mb-5" />
            <h1 className="font-serif text-3xl font-semibold text-foreground mb-2">Still processing…</h1>
            <p className="text-muted-foreground mb-6">Your payment is taking a little longer. We'll email you once it's confirmed.</p>
            <Button asChild variant="outline" className="rounded-full w-full h-11"><Link to="/">Back to home</Link></Button>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="h-16 w-16 text-destructive mx-auto mb-5" />
            <h1 className="font-serif text-3xl font-semibold text-foreground mb-2">Payment not completed</h1>
            <p className="text-muted-foreground mb-6">We couldn't confirm your payment. Please try booking again.</p>
            <Button asChild className="rounded-full bg-primary w-full h-11"><Link to="/#book">Try again</Link></Button>
          </>
        )}
      </div>
    </div>
  );
}
